/** An array value serializes as REPEATED query keys (`?k=a&k=b`) — the typed
 * multi-value representation (e.g. the KPI `ownerUserIds` filter, PB-2941).
 * Empty arrays and empty strings are dropped like undefined. */
export type ApiQueryValue = string | number | readonly string[] | undefined;

/**
 * How a `query` entry merges with a same-named parameter ALREADY on the path
 * (`buildApiUrl(base, "/v1/kpis?ownerUserIds=z", …)`). All three rules are about
 * that collision; with no pre-existing parameter they are indistinguishable,
 * which is why they are stated here and pinned in `url.spec.ts`.
 *
 * - **A value REPLACES.** A scalar `set`s and an array `delete`s-then-`append`s,
 *   so `?k=z` + `{ k: ["a","b"] }` is `?k=a&k=b`, never `?k=z&k=a&k=b`. The
 *   caller's explicit filter wins over whatever the path carried, and a
 *   multi-value filter cannot silently accumulate across calls.
 * - **Nothing-to-contribute is a NO-OP, not a clear.** `undefined`, `""`, `[]`
 *   and an array whose every entry is `""` all leave a pre-existing parameter
 *   exactly as it was. "Absent" has to mean "I am not expressing an opinion" —
 *   if `[]` cleared, an unset multi-select would erase a filter the caller
 *   deliberately baked into the path, and it would do so differently from the
 *   `undefined` the same unset control produces elsewhere.
 * - **Empty entries are dropped INDIVIDUALLY.** `["a", ""]` contributes `k=a`
 *   alone, so one blank option never becomes a `k=` the server has to interpret.
 *
 * There is deliberately no append/merge mode. A second one would make the
 * outcome depend on how the base path happened to be written.
 */

export type ApiQuery = Record<string, ApiQueryValue>;

const ABSOLUTE_URL_RE = /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//;

export function buildApiUrl(
  baseUrl: string,
  path: string,
  query?: ApiQuery,
): string {
  const joined = joinBaseAndPath(baseUrl, path);
  const parts = joined.split("?", 2);
  const pathname = parts[0] ?? "";
  const existingQuery = parts[1] ?? "";
  const params = new URLSearchParams(existingQuery);

  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === "") continue;
      if (Array.isArray(value)) {
        const entries = value.filter((entry) => entry !== "");
        // Nothing to contribute → behave exactly like `undefined` and leave any
        // pre-existing parameter alone. Deleting first and then appending
        // nothing would make `[]` a CLEAR, which is a third semantic no caller
        // asked for and the opposite of what `""` does one line above.
        if (entries.length === 0) continue;
        params.delete(key);
        for (const entry of entries) params.append(key, entry);
        continue;
      }
      params.set(key, String(value));
    }
  }

  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

function joinBaseAndPath(baseUrl: string, path: string): string {
  const base = baseUrl.trim();
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  if (ABSOLUTE_URL_RE.test(base)) {
    return `${base.replace(/\/+$/, "")}${normalizedPath}`;
  }

  if (!base || base === "/") {
    return normalizedPath;
  }

  const normalizedBase = `/${base.replace(/^\/+/, "").replace(/\/+$/, "")}`;
  return `${normalizedBase}${normalizedPath}`;
}
